// useNotification.ts
import { useCallback } from "react";
import { NotificationOptions2 } from "service/notification";

interface UseNotificationReturn {
  notify: (
    title: string,
    options?: NotificationOptions2
  ) => Promise<Notification | null>;
  checkPermission: () => Promise<boolean>;
  isSupported: boolean;
}

const useNotification = (): UseNotificationReturn => {
  const isSupported = "Notification" in window;

  const checkPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      console.log("This browser does not support desktop notification");
      return false;
    }

    let permission = Notification.permission;

    if (permission !== "granted" && permission !== "denied") {
      permission = await Notification.requestPermission();
    }

    return permission === "granted";
  }, [isSupported]);

  const notify = useCallback(
    async (
      title: string,
      options: NotificationOptions = {}
    ): Promise<Notification | null> => {
      const hasPermission = await checkPermission();

      if (hasPermission) {
        const notification = new Notification(title, options);

        notification.onclick = () => {
          window.focus();
          notification.close();
        };

        return notification;
      }

      return null;
    },
    [checkPermission]
  );

  return { notify, checkPermission, isSupported };
};

export default useNotification;
